import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { useMonth } from '@/contexts/MonthContext';
import { buildExtendedMonthList } from '@/lib/months';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, Edit2, Trash2, Plus, Filter, ChevronLeft, ChevronRight, Calendar, FileText, Check, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { toast } from 'sonner';
import { fmtMoney, getDisplayCurrency } from '@/lib/currency';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';

const fmt = (n) => fmtMoney(n, null, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const getML = (y, m) => `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
const navM = (y, m, d) => { let mi = parseInt(m, 10) + d, yi = parseInt(y, 10); while (mi > 12) { mi -= 12; yi++; } while (mi < 1) { mi += 12; yi--; } return { year: String(yi), month: String(mi).padStart(2, '0') }; };

export default function TransactionsPage() {
  useDisplayCurrency(); // re-render when user changes base currency
  const [searchParams, setSearchParams] = useSearchParams();
  const [txns, setTxns] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [catFilter, setCatFilter] = useState(searchParams.get('category') || searchParams.get('categories') || '');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTxn, setEditTxn] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [availableMonths, setAvailableMonths] = useState([]);
  const { year: ctxYear, month: ctxMonth, setMonth: ctxSetMonth } = useMonth();
  // URL params override context for deep-link compat; otherwise sync with global month
  const [currentYear, setCurrentYearState] = useState(searchParams.get('year') || ctxYear);
  const [currentMonth, setCurrentMonthState] = useState(searchParams.get('month') || ctxMonth);
  const setCurrentYear = (v) => { setCurrentYearState(v); if (v && currentMonth) ctxSetMonth(v, currentMonth); };
  const setCurrentMonth = (v) => { setCurrentMonthState(v); if (v && currentYear) ctxSetMonth(currentYear, v); };
  const [assets, setAssets] = useState([]);
  const [liabilities, setLiabilities] = useState([]);
  const [sourceFilter, setSourceFilter] = useState('all'); // all | manual | imported
  
  // Custom date range state
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDateRange, setShowDateRange] = useState(false);
  const [showDateDialog, setShowDateDialog] = useState(false);
  
  // Multi-select state
  const [selectedTxnIds, setSelectedTxnIds] = useState([]);

  // Statement filter — must be declared before load useCallback
  const [typeFilter, setTypeFilter] = useState('all'); // all | debit | credit
  const [statementFilter, setStatementFilter] = useState(''); // '' = all | statement _id
  const [statements, setStatements] = useState([]);

  useEffect(() => {
    api.get('/api/dashboard/available-months').then(({ data }) => {
      setAvailableMonths(data || []);
      if (!currentYear && !currentMonth && data.length > 0) {
        const [y, m] = data[0].month.split('-');
        setCurrentYear(y);
        setCurrentMonth(m);
      }
    }).catch(() => {});
    api.get('/api/categories').then(({ data }) => setCategories(data)).catch(() => {});
    api.get('/api/networth/assets').then(({ data }) => setAssets(data || [])).catch(() => {});
    api.get('/api/networth/liabilities').then(({ data }) => setLiabilities(data || [])).catch(() => {});
    api.get('/api/statements').then(({ data }) => setStatements(data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (search) params.search = search;
      // Handle grouped categories vs single category
      if (catFilter) {
        const isGrouped = catFilter.includes(',');
        if (isGrouped) {
          params.categories = catFilter; // Grouped categories (comma-separated)
        } else {
          params.category = catFilter; // Single category
        }
      }
      // Statement filter: pass alongside month/year for AND logic
      // (statement_id + month = only that statement's transactions in that month)
      if (statementFilter) {
        params.statement_id = statementFilter;
      }
      // Date range / month always applies independently (AND with statement when both set)
      if (showDateRange && (dateFrom || dateTo)) {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      } else if (currentYear && currentMonth) {
        params.year = currentYear;
        params.month = currentMonth;
      }
      const { data } = await api.get('/api/transactions', { params });
      setTxns(data.transactions || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [page, search, catFilter, currentYear, currentMonth, showDateRange, dateFrom, dateTo, statementFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`/api/transactions/${id}`); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
  };
  
  // Multi-select handlers
  const toggleSelectAll = () => {
    if (selectedTxnIds.length === txns.length) {
      setSelectedTxnIds([]);
    } else {
      setSelectedTxnIds(txns.map(t => t._id));
    }
  };
  
  const toggleSelectTxn = (txnId) => {
    setSelectedTxnIds(prev => 
      prev.includes(txnId) 
        ? prev.filter(id => id !== txnId)
        : [...prev, txnId]
    );
  };
  
  const bulkDelete = async () => {
    const msg = `⚠️ Delete ${selectedTxnIds.length} transaction(s)?\n\nThis action cannot be undone!`;
    if (!window.confirm(msg)) return;
    
    try {
      await api.post('/api/transactions/bulk/delete', { transaction_ids: selectedTxnIds });
      toast.success(`Deleted ${selectedTxnIds.length} transaction(s)`);
      setSelectedTxnIds([]);
      load();
    } catch (e) { 
      toast.error(e.response?.data?.detail || 'Failed to delete'); 
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      // v1.4.3 §1 — Editing a virtual recurring projection creates a per-month override
      // (POST a new real transaction with recurring_source_id) instead of mutating the source.
      if (editTxn && editTxn.is_virtual) {
        const overrideTxn = {
          date: editTxn.date,
          merchant: data.merchant || editTxn.merchant,
          description: data.description || editTxn.description || data.merchant,
          amount: parseFloat(data.amount ?? editTxn.amount),
          transaction_type: data.transaction_type || editTxn.transaction_type || 'debit',
          category: data.category || editTxn.category || 'Miscellaneous',
          original_currency: editTxn.original_currency || getDisplayCurrency(),
          linked_asset_id: editTxn.linked_asset_id || null,
          linked_liability_id: editTxn.linked_liability_id || null,
          is_repayment: !!editTxn.is_repayment,
          is_investment: !!editTxn.is_investment,
          is_recurring: false,
          recurring_source_id: editTxn.recurring_source_id,
          notes: data.notes ?? editTxn.notes ?? null,
        };
        await api.post('/api/transactions', overrideTxn);
        toast.success('Override saved for this month');
        setEditTxn(null);
        load();
        return;
      }
      await api.put(`/api/transactions/${id}`, data);
      toast.success('Updated');
      setEditTxn(null);
      load();
    } catch { toast.error('Failed'); }
  };

  const handleDeleteStatement = async (stmtId, filename) => {
    if (!window.confirm(`Delete "${filename}" and ALL its transactions? This will be reflected across the app.`)) return;
    try {
      const { data } = await api.delete(`/api/statements/${stmtId}`);
      toast.success(data.message);
      setStatements((prev) => prev.filter((s) => s._id !== stmtId));
      load();
    } catch { toast.error('Failed to delete statement'); }
  };

  const handlePrev = () => { if (currentYear && currentMonth) { const n = navM(currentYear, currentMonth, -1); setCurrentYear(n.year); setCurrentMonth(n.month); setPage(1); } };
  const handleNext = () => { if (currentYear && currentMonth) { const n = navM(currentYear, currentMonth, 1); setCurrentYear(n.year); setCurrentMonth(n.month); setPage(1); } };

  const handleDateRangeApply = () => {
    if (!dateFrom && !dateTo) {
      toast.error('Please select at least one date');
      return false;
    }
    if (dateFrom && dateTo && dateFrom > dateTo) {
      toast.error('Start date must be before end date');
      return false;
    }
    setShowDateRange(true);
    setShowDateDialog(false);
    setPage(1);
    return true;
  };

  const handleDateRangeClear = () => {
    setDateFrom('');
    setDateTo('');
    setShowDateRange(false);
    setPage(1);
  };

  const monthLabel = currentYear && currentMonth ? getML(currentYear, currentMonth) : 'All';
  const filteredTxns = useMemo(() => {
    let list = txns;
    if (sourceFilter === 'manual') list = list.filter((t) => t.is_manual);
    if (sourceFilter === 'imported') list = list.filter((t) => !t.is_manual);
    if (typeFilter === 'debit') list = list.filter((t) => t.transaction_type === 'debit');
    if (typeFilter === 'credit') list = list.filter((t) => t.transaction_type === 'credit');
    return list;
  }, [txns, sourceFilter, typeFilter]);
  const totalExpenses = filteredTxns.filter(t => t.transaction_type === 'debit').reduce((s, t) => s + t.amount, 0);
  const extendedMonths = useMemo(() => buildExtendedMonthList(availableMonths, 24, 12), [availableMonths]);

  const activeStatementName = statementFilter
    ? (statements.find(s => s._id === statementFilter)?.filename.replace(/\.[^.]+$/, '').slice(0, 30) || null)
    : null;

  return (
    <div className="space-y-6" data-testid="transactions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-['Outfit']">Transactions</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total} transactions &middot; {fmt(totalExpenses)} spent
            {activeStatementName && <span className="ml-1">· from <span className="font-medium text-foreground">{activeStatementName}</span></span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!showDateRange ? (
            <>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={handlePrev} data-testid="txn-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
              {extendedMonths.length > 0 && (
                <Select value={currentYear && currentMonth ? `${currentYear}-${currentMonth}` : ''} onValueChange={(v) => { const [y, m] = v.split('-'); setCurrentYear(y); setCurrentMonth(m); setPage(1); setShowDateRange(false); }}>
                  <SelectTrigger className="w-[160px] h-8 text-sm" data-testid="txn-month-selector"><Calendar className="h-3.5 w-3.5 mr-1.5" /><SelectValue placeholder={monthLabel} /></SelectTrigger>
                  <SelectContent className="max-h-[400px]">
                    {extendedMonths.map((em) => (
                      <SelectItem key={em.month} value={em.month}>
                        <span className={em.hasData ? '' : 'text-muted-foreground'}>{em.label}</span>
                        {em.hasData && <span className="text-xs text-muted-foreground ml-1">({em.count})</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleNext} data-testid="txn-next-month"><ChevronRight className="h-4 w-4" /></Button>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-sm">
                {dateFrom && dateTo ? `${dateFrom} to ${dateTo}` : dateFrom ? `From ${dateFrom}` : `Until ${dateTo}`}
              </Badge>
              <Button variant="ghost" size="sm" onClick={handleDateRangeClear}>Clear</Button>
            </div>
          )}
          <Dialog open={showDateDialog} onOpenChange={setShowDateDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="h-8">
                <Calendar className="h-3.5 w-3.5 mr-1.5" />
                Custom Range
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle className="font-['Outfit']">Custom Date Range</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="date-from">From Date</Label>
                  <Input 
                    id="date-from" 
                    type="date" 
                    value={dateFrom} 
                    onChange={(e) => setDateFrom(e.target.value)}
                    max={dateTo || undefined}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date-to">To Date</Label>
                  <Input 
                    id="date-to" 
                    type="date" 
                    value={dateTo} 
                    onChange={(e) => setDateTo(e.target.value)}
                    min={dateFrom || undefined}
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => setShowDateDialog(false)}>Cancel</Button>
                  <Button size="sm" onClick={handleDateRangeApply}>Apply</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild><Button size="sm" data-testid="add-transaction-btn"><Plus className="h-4 w-4 mr-1" />Add</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle className="font-['Outfit']">Add Transaction</DialogTitle></DialogHeader><AddTxnForm categories={categories} assets={assets} liabilities={liabilities} defaultDate={currentYear && currentMonth ? `${currentYear}-${currentMonth}-${String(new Date().getDate()).padStart(2,'0')}` : new Date().toISOString().split('T')[0]} onSave={async () => { await load(); setShowAdd(false); }} /></DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="txn-search-input" placeholder="Search..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-9" />
        </div>
        {catFilter && catFilter.includes(',') ? (
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-sm">
              Dining Out (3 categories)
            </Badge>
            <Button variant="ghost" size="sm" onClick={() => { setCatFilter(''); setPage(1); }}>Clear</Button>
          </div>
        ) : (
          <CategoryCombobox
            categories={categories}
            value={catFilter}
            onChange={(v) => { setCatFilter(v); setPage(1); }}
          />
        )}
        <div className="flex gap-1 p-1 rounded-md bg-muted/50" data-testid="source-filter">
          {[
            { v: 'all', l: 'All' },
            { v: 'imported', l: 'Imported' },
            { v: 'manual', l: 'Manual' },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setSourceFilter(opt.v)}
              data-testid={`source-filter-${opt.v}`}
              className={`text-xs px-3 py-1 rounded transition-colors ${sourceFilter === opt.v ? 'bg-card shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}
            >{opt.l}</button>
          ))}
        </div>
        <div className="flex gap-1 p-1 rounded-md bg-muted/50" data-testid="type-filter">
          {[
            { v: 'all', l: 'All Types' },
            { v: 'debit', l: 'Debits' },
            { v: 'credit', l: 'Credits' },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => { setTypeFilter(opt.v); setPage(1); }}
              data-testid={`type-filter-${opt.v}`}
              className={`text-xs px-3 py-1 rounded transition-colors ${typeFilter === opt.v ? 'bg-card shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}
            >{opt.l}</button>
          ))}
        </div>

        {/* Statement filter — filter transactions by uploaded PDF/CSV */}
        {statements.length > 0 && (
          <div className="flex items-center gap-1.5" data-testid="statement-filter">
            <Select
              value={statementFilter || 'all'}
              onValueChange={(v) => { setStatementFilter(v === 'all' ? '' : v); setPage(1); }}
            >
              <SelectTrigger className="h-8 text-xs w-auto max-w-[220px] gap-1.5" data-testid="statement-filter-select">
                <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="truncate flex-1 text-left">
                  {statementFilter
                    ? (statements.find(s => s._id === statementFilter)?.filename.replace(/\.[^.]+$/, '').slice(0, 22) || 'Statement')
                    : 'All Statements'}
                </span>
              </SelectTrigger>
              <SelectContent align="start" className="max-w-[300px]">
                <SelectItem value="all" data-testid="statement-filter-all">
                  <span className="text-xs">All Statements</span>
                </SelectItem>
                {statements.map((s) => (
                  <SelectItem key={s._id} value={s._id} data-testid={`statement-filter-${s._id}`}>
                    <div className="flex flex-col py-0.5 max-w-[260px]">
                      <span className="text-xs font-medium truncate">{s.filename.replace(/\.[^.]+$/, '')}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {s.transaction_count ?? '?'} txns · {new Date(s.upload_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {statementFilter && (
              <button
                type="button"
                onClick={() => { setStatementFilter(''); setPage(1); }}
                className="h-8 w-8 flex items-center justify-center rounded hover:bg-muted transition-colors"
                data-testid="statement-filter-clear"
                title="Clear statement filter"
              >
                <X className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            )}
          </div>
        )}
      </div>

      <Card className="border border-border shadow-none">
        {/* Bulk Actions Toolbar */}
        {selectedTxnIds.length > 0 && (
          <CardContent className="py-2.5 px-4 bg-accent/30 border-b">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{selectedTxnIds.length} transaction(s) selected</span>
              <Button variant="destructive" size="sm" onClick={bulkDelete} className="h-7 text-xs">
                <Trash2 className="h-3 w-3 mr-1" />Delete Selected
              </Button>
            </div>
          </CardContent>
        )}
        
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <Checkbox
                    checked={selectedTxnIds.length === filteredTxns.length && filteredTxns.length > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                </TableHead>
                <TableHead className="w-[100px]">Date</TableHead>
                <TableHead>Merchant</TableHead>
                <TableHead className="hidden md:table-cell">Description</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? [...Array(5)].map((_, i) => <TableRow key={i}><TableCell colSpan={7}><div className="h-8 bg-muted rounded animate-pulse" /></TableCell></TableRow>)
              : filteredTxns.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">{sourceFilter !== 'all' ? `No ${sourceFilter} transactions for ${monthLabel}` : `No transactions for ${monthLabel}`}</TableCell></TableRow>
              : filteredTxns.map((t) => (
                <TableRow key={t._id} className={t.transaction_type === 'credit' ? 'bg-green-50/50 dark:bg-green-900/5' : ''} data-testid={`txn-row-${t._id}`}>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedTxnIds.includes(t._id)}
                      onCheckedChange={() => toggleSelectTxn(t._id)}
                    />
                  </TableCell>
                  <TableCell className="text-xs tabular-nums whitespace-nowrap">{t.date}</TableCell>
                  <TableCell className="font-medium text-sm">{t.normalized_merchant || t.merchant}</TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-muted-foreground truncate max-w-[200px]">{t.description}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 flex-wrap">
                      <Badge variant="secondary" className="text-xs whitespace-nowrap">{t.category}</Badge>
                      {t.transaction_type === 'credit' && <Badge className="text-[10px] h-4 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-0">Refund</Badge>}
                      {t.is_repayment && <Badge className="text-[10px] h-4 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-0" data-testid={`repayment-badge-${t._id}`}>Repayment</Badge>}
                      {t.is_investment && <Badge className="text-[10px] h-4 bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 border-0" data-testid={`investment-badge-${t._id}`}>Investment</Badge>}
                      {!t.is_repayment && !t.is_investment && (t.linked_asset_id || t.linked_liability_id) && <Badge className="text-[10px] h-4 bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 border-0" data-testid={`linked-badge-${t._id}`}>Linked</Badge>}
                      {t.is_manual && <Badge className="text-[10px] h-4 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-0" data-testid={`manual-badge-${t._id}`}>Manual</Badge>}
                      {(t.is_recurring || t.is_virtual) && <Badge className="text-[10px] h-4 bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400 border-0" data-testid={`recurring-badge-${t._id}`}>{t.is_virtual ? 'Scheduled' : 'Recurring'}</Badge>}
                      {t.auto_generated && !t.is_virtual && (
                        <Badge className={`text-[10px] h-4 border-0 ${t.date <= new Date().toISOString().split('T')[0] ? 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-400' : 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-400'}`} data-testid={`auto-generated-badge-${t._id}`}>
                          {t.date <= new Date().toISOString().split('T')[0] ? 'Auto-paid' : 'Scheduled'}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className={`text-right tabular-nums font-medium text-sm ${t.transaction_type === 'credit' ? 'text-green-600 dark:text-green-400' : ''}`}>
                    {t.transaction_type === 'credit' ? '+' : '-'}{fmtMoney(t.amount, t.original_currency || null, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {t.is_virtual ? (
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditTxn(t)} data-testid={`edit-txn-${t._id}`} title="Override for this month only"><Edit2 className="h-3 w-3" /></Button>
                      ) : (
                        <>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditTxn(t)} data-testid={`edit-txn-${t._id}`}><Edit2 className="h-3 w-3" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(t._id)} data-testid={`delete-txn-${t._id}`}><Trash2 className="h-3 w-3" /></Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {pages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
          <span className="flex items-center text-sm text-muted-foreground tabular-nums">Page {page} of {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      )}

      {/* Uploaded statements UI removed from Transactions page (v1.3.1 §2). Manage via /upload. */}

      <Dialog open={!!editTxn} onOpenChange={(o) => !o && setEditTxn(null)}>
        <DialogContent><DialogHeader><DialogTitle className="font-['Outfit']">{editTxn?.is_virtual ? 'Override This Month' : 'Edit Transaction'}</DialogTitle></DialogHeader>{editTxn && <EditTxnForm txn={editTxn} categories={categories} onSave={handleUpdate} onClose={() => { setEditTxn(null); load(); }} />}</DialogContent>
      </Dialog>
    </div>
  );
}

function CategoryCombobox({ categories, value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = value || '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[180px] justify-between font-normal"
          data-testid="txn-category-filter"
        >
          <div className="flex items-center gap-2 truncate">
            <Filter className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{selected || 'All Categories'}</span>
          </div>
          <ChevronLeft className="ml-1 h-3.5 w-3.5 rotate-[-90deg] text-muted-foreground shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search category…" className="h-8 text-sm" data-testid="category-search-input" />
          <CommandList>
            <CommandEmpty>No category found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="all"
                onSelect={() => { onChange(''); setOpen(false); }}
                data-testid="category-option-all"
              >
                <Check className={`mr-2 h-3.5 w-3.5 ${!selected ? 'opacity-100' : 'opacity-0'}`} />
                All Categories
              </CommandItem>
              {categories.map((c) => (
                <CommandItem
                  key={c}
                  value={c}
                  onSelect={(v) => { onChange(v === selected ? '' : v); setOpen(false); }}
                  data-testid={`category-option-${c}`}
                >
                  <Check className={`mr-2 h-3.5 w-3.5 ${selected === c ? 'opacity-100' : 'opacity-0'}`} />
                  {c}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function AddTxnForm({ categories, onSave, defaultDate, assets = [], liabilities = [] }) {
  const baseCurrency = getDisplayCurrency(); // Get user's base currency
  const [mode, setMode] = useState('normal'); // normal | repayment | investment
  const [form, setForm] = useState({
    date: defaultDate || new Date().toISOString().split('T')[0],
    merchant: '',
    description: '',
    amount: '',
    transaction_type: 'debit',
    category: 'Miscellaneous',
    linked_asset_id: '',
    linked_liability_id: '',
    original_currency: baseCurrency,
    is_recurring: false,
    recurring_frequency: 'monthly',
    recurring_end_date: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const investmentAssets = assets.filter((a) => ['Investment', 'TFSA', 'RRSP', 'Stocks', 'Crypto', 'Mutual Fund', 'ETF', 'Bonds'].includes(a.asset_type));

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        linked_asset_id: form.linked_asset_id || null,
        linked_liability_id: form.linked_liability_id || null,
        is_repayment: mode === 'repayment',
        is_investment: mode === 'investment',
        is_recurring: !!form.is_recurring,
        recurring_frequency: form.is_recurring ? (form.recurring_frequency || 'monthly') : 'monthly',
        recurring_end_date: form.is_recurring && form.recurring_end_date ? form.recurring_end_date : null,
      };
      if (mode === 'repayment') {
        if (!payload.linked_liability_id) { toast.error('Pick the liability you paid'); setSaving(false); return; }
        payload.linked_asset_id = null;
        if (!payload.merchant) payload.merchant = 'Liability Repayment';
        payload.transaction_type = 'debit';
      }
      if (mode === 'investment') {
        if (!payload.linked_asset_id) { toast.error('Pick an investment to add to'); setSaving(false); return; }
        payload.linked_liability_id = null;
        payload.category = 'Investments';
        if (!payload.merchant) payload.merchant = 'Investment / SIP';
        payload.transaction_type = 'debit';
      }
      await api.post('/api/transactions', payload);
      toast.success(mode === 'repayment' ? 'Repayment recorded' : mode === 'investment' ? 'Investment recorded' : 'Added');
      await onSave();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="flex gap-1.5 p-1 rounded-md bg-muted/50 w-fit" data-testid="add-txn-mode-toggle">
        <button type="button" onClick={() => { setMode('normal'); set('category', 'Miscellaneous'); }} className={`text-xs px-3 py-1 rounded ${mode === 'normal' ? 'bg-card shadow-sm font-medium' : 'text-muted-foreground'}`} data-testid="mode-normal">Normal</button>
        <button type="button" onClick={() => { setMode('repayment'); set('category', 'Loan Repayment'); }} className={`text-xs px-3 py-1 rounded ${mode === 'repayment' ? 'bg-card shadow-sm font-medium' : 'text-muted-foreground'}`} data-testid="mode-repayment">Repayment</button>
        <button type="button" onClick={() => setMode('investment')} className={`text-xs px-3 py-1 rounded ${mode === 'investment' ? 'bg-card shadow-sm font-medium' : 'text-muted-foreground'}`} data-testid="mode-investment">Investment / SIP</button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs">Date</Label><Input data-testid="add-txn-date" type="date" value={form.date} onChange={(e) => set('date', e.target.value)} required /></div>
        {mode !== 'investment' && (
          <div className="space-y-1"><Label className="text-xs">Amount</Label><Input data-testid="add-txn-amount" type="number" step="0.01" value={form.amount} onChange={(e) => set('amount', e.target.value)} required /></div>
        )}
      </div>

      {mode === 'normal' && (
        <>
          <div className="space-y-1"><Label className="text-xs">Merchant</Label><Input data-testid="add-txn-merchant" value={form.merchant} onChange={(e) => set('merchant', e.target.value)} required /></div>
          <div className="space-y-1"><Label className="text-xs">Description</Label><Input data-testid="add-txn-description" value={form.description} onChange={(e) => set('description', e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Type</Label><Select value={form.transaction_type} onValueChange={(v) => set('transaction_type', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="debit">Debit</SelectItem><SelectItem value="credit">Credit</SelectItem></SelectContent></Select></div>
            <div className="space-y-1"><Label className="text-xs">Category</Label><Select value={form.category} onValueChange={(v) => set('category', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Linked Account <span className="text-muted-foreground">(optional — auto-updates Net Worth)</span></Label>
            <Select value={form.linked_asset_id || 'none'} onValueChange={(v) => set('linked_asset_id', v === 'none' ? '' : v)}>
              <SelectTrigger data-testid="add-txn-linked-asset"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {assets.map((a) => <SelectItem key={a._id} value={a._id}>{a.name} ({new Intl.NumberFormat('en-CA', { style: 'currency', currency: a.currency || baseCurrency, maximumFractionDigits: 0 }).format(a.value || 0)})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      {mode === 'repayment' && (
        <>
          <div className="p-2.5 rounded-md bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-300">
            Pay down a liability (loan, credit card, etc.) from income. Only the liability balance will be reduced.
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Liability paid</Label>
            <Select value={form.linked_liability_id || ''} onValueChange={(v) => set('linked_liability_id', v)}>
              <SelectTrigger data-testid="repayment-to-liability"><SelectValue placeholder="Select liability..." /></SelectTrigger>
              <SelectContent>{liabilities.map((l) => <SelectItem key={l._id} value={l._id}>{l.name} ({new Intl.NumberFormat('en-CA', { style: 'currency', currency: l.currency || baseCurrency, maximumFractionDigits: 0 }).format(l.amount || 0)} outstanding)</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Category</Label>
            <Select value={form.category || 'Loan Repayment'} onValueChange={(v) => set('category', v)}>
              <SelectTrigger data-testid="repayment-category"><SelectValue /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Note (optional)</Label><Input data-testid="repayment-note" value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="e.g. June EMI" /></div>
        </>
      )}

      {mode === 'investment' && (
        <>
          <div className="p-2.5 rounded-md bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800 text-xs text-emerald-700 dark:text-emerald-300">
            Log a contribution (SIP, top-up, share purchase). The selected investment asset balance will increase by this amount.
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Investment / Portfolio</Label>
            <Select
              value={form.linked_asset_id || ''}
              onValueChange={(v) => {
                const asset = investmentAssets.find((a) => a._id === v);
                set('linked_asset_id', v);
                if (asset?.currency) set('original_currency', asset.currency);
              }}
            >
              <SelectTrigger data-testid="investment-target-asset"><SelectValue placeholder={investmentAssets.length ? 'Select investment...' : 'No investment assets — add one in Net Worth first'} /></SelectTrigger>
              <SelectContent>{investmentAssets.map((a) => <SelectItem key={a._id} value={a._id}>{a.name} · {a.asset_type} ({new Intl.NumberFormat('en-CA', { style: 'currency', currency: a.currency || baseCurrency, maximumFractionDigits: 0 }).format(a.value || 0)})</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {/* Amount + Currency side-by-side so user sees both together */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Amount <span className="font-semibold text-emerald-700 dark:text-emerald-400">({form.original_currency || baseCurrency})</span></Label>
              <Input data-testid="add-txn-amount" type="number" step="0.01" value={form.amount} onChange={(e) => set('amount', e.target.value)} placeholder={`e.g. 500`} required />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Currency</Label>
              <Select value={form.original_currency || baseCurrency} onValueChange={(v) => set('original_currency', v)}>
                <SelectTrigger data-testid="investment-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['CAD', 'USD', 'INR', 'GBP', 'EUR', 'AUD', 'SGD', 'AED', 'JPY'].map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.original_currency && form.original_currency !== baseCurrency && form.amount && parseFloat(form.amount) > 0 && (
            <p className="text-[11px] text-emerald-700 dark:text-emerald-400 -mt-1">
              {form.amount} {form.original_currency} will be converted to {baseCurrency} using live rates for chart display.
            </p>
          )}

          <div className="space-y-1"><Label className="text-xs">Merchant / Source (optional)</Label><Input data-testid="investment-merchant" value={form.merchant} onChange={(e) => set('merchant', e.target.value)} placeholder="e.g. Wealthsimple, Zerodha" /></div>
          <div className="space-y-1"><Label className="text-xs">Note (optional)</Label><Input data-testid="investment-note" value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="e.g. Monthly SIP" /></div>
        </>
      )}

      {/* Recurring schedule — Normal and Repayment modes */}
      {(mode === 'normal' || mode === 'repayment') && (
      <div className="p-2.5 rounded-md bg-muted/30 space-y-2" data-testid="recurring-block">
        <div className="flex items-center gap-2">
          <input type="checkbox" id="add-txn-recurring" checked={!!form.is_recurring} onChange={(e) => set('is_recurring', e.target.checked)} data-testid="add-txn-recurring-toggle" className="h-3.5 w-3.5" />
          <Label htmlFor="add-txn-recurring" className="text-xs cursor-pointer">
            {mode === 'repayment' ? 'Schedule as recurring repayment (EMI, car payment, loan)' : 'Repeat on a schedule (rent, EMI, subscription)'}
          </Label>
        </div>
        {form.is_recurring && (
          <div className="grid grid-cols-2 gap-2 pl-6">
            <div className="space-y-1 col-span-2">
              <Label className="text-[10px]">Frequency</Label>
              <Select value={form.recurring_frequency || 'monthly'} onValueChange={(v) => set('recurring_frequency', v)}>
                <SelectTrigger className="h-7 text-xs" data-testid="add-txn-recurring-frequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="biweekly">Bi-weekly (every 2 weeks)</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px]">First payment date</Label>
              <Input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} className="h-7 text-xs" data-testid="add-txn-recurring-start" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px]">End date (optional)</Label>
              <Input type="date" value={form.recurring_end_date} onChange={(e) => set('recurring_end_date', e.target.value)} className="h-7 text-xs" data-testid="add-txn-recurring-end" placeholder="Leave blank for indefinite" />
            </div>
            <p className="col-span-2 text-[10px] text-muted-foreground">
              Transactions will be auto-created on each due date and update your net worth automatically.
            </p>
          </div>
        )}
      </div>
      )}

      <Button type="submit" className="w-full" disabled={saving} data-testid="add-txn-submit">{saving ? 'Adding...' : (mode === 'repayment' ? 'Record Repayment' : mode === 'investment' ? 'Add Investment' : 'Add Transaction')}</Button>
    </form>
  );
}

function EditTxnForm({ txn, categories, onSave, onClose }) {
  const [form, setForm] = useState({
    date: txn.date,
    amount: txn.amount,
    transaction_type: txn.transaction_type,
    category: txn.category,
    merchant: txn.merchant,
    description: txn.description || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Recurring schedule editing
  const isRecurringSeries = txn.is_recurring || (txn.auto_generated && txn.recurring_source_id);
  const [scheduleEndDate, setScheduleEndDate] = useState(txn.recurring_end_date || '');
  const [savingSchedule, setSavingSchedule] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await onSave(txn._id, { ...form, amount: parseFloat(form.amount) }); } finally { setSaving(false); }
  };

  const handleUpdateSchedule = async () => {
    setSavingSchedule(true);
    try {
      await api.put(`/api/transactions/${txn._id}/recurring-end-date`, {
        end_date: scheduleEndDate || null,
      });
      toast.success('Recurring schedule updated');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      {txn.is_virtual && (
        <div className="text-[11px] p-2 rounded bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-900" data-testid="override-banner">
          You&apos;re editing a single month of a recurring schedule. Saving creates a one-off override; future months keep the original amount.
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs">Date</Label><Input data-testid="edit-txn-date" type="date" value={form.date} onChange={(e) => set('date', e.target.value)} required /></div>
        <div className="space-y-1"><Label className="text-xs">Amount</Label><Input data-testid="edit-txn-amount" type="number" step="0.01" value={form.amount} onChange={(e) => set('amount', e.target.value)} required /></div>
      </div>
      <div className="space-y-1"><Label className="text-xs">Merchant</Label><Input data-testid="edit-txn-merchant" value={form.merchant} onChange={(e) => set('merchant', e.target.value)} /></div>
      <div className="space-y-1"><Label className="text-xs">Description</Label><Input data-testid="edit-txn-description" value={form.description} onChange={(e) => set('description', e.target.value)} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs">Type</Label><Select value={form.transaction_type} onValueChange={(v) => set('transaction_type', v)}><SelectTrigger data-testid="edit-txn-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="debit">Debit</SelectItem><SelectItem value="credit">Credit</SelectItem></SelectContent></Select></div>
        <div className="space-y-1"><Label className="text-xs">Category</Label><Select value={form.category} onValueChange={(v) => set('category', v)}><SelectTrigger data-testid="edit-txn-category"><SelectValue /></SelectTrigger><SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <Button type="submit" className="w-full" disabled={saving} data-testid="edit-txn-submit">{saving ? 'Saving...' : 'Save Changes'}</Button>

      {/* ── Recurring Schedule Editor ─────────────────────────────── */}
      {isRecurringSeries && (
        <div className="border-t pt-3 mt-1 space-y-2" data-testid="recurring-schedule-editor">
          <div>
            <p className="text-xs font-medium">Edit Recurring Schedule</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Change the end date for the <em>entire</em> series.
              Future unpaid instances are added or removed automatically.
            </p>
          </div>
          <div className="flex gap-2 items-end">
            <div className="flex-1 space-y-1">
              <Label className="text-[11px]">Series end date</Label>
              <Input
                type="date"
                value={scheduleEndDate}
                onChange={(e) => setScheduleEndDate(e.target.value)}
                className="h-8 text-xs"
                data-testid="edit-recurring-end-date"
                placeholder="Leave blank for open-ended"
              />
              {!scheduleEndDate && (
                <p className="text-[10px] text-muted-foreground">Leave blank → generates 24 months ahead</p>
              )}
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mb-0.5"
              onClick={handleUpdateSchedule}
              disabled={savingSchedule}
              data-testid="update-recurring-schedule-btn"
            >
              {savingSchedule ? 'Updating…' : 'Update Schedule'}
            </Button>
          </div>
        </div>
      )}
    </form>
  );
}
